import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import {
  FaCamera,
  FaCheck,
  FaImages,
  FaRedo,
  FaSyncAlt,
  FaTimes,
  FaTrash,
  FaUndoAlt,
} from "react-icons/fa";

/* =========================================================
   CONFIG
========================================================= */

const MAX_IMAGES = 5;

/*
 * Jewellery focused presets.
 *
 * Ye Instagram-type artistic filters nahi hain.
 * Mainly jewellery ko clean / bright / premium dikhane ke liye hain.
 */
const FILTERS = [
  {
    key: "natural",
    label: "Natural",
    brightness: 100,
    contrast: 100,
    saturation: 100,
    warmth: 0,
  },

  {
    key: "bright",
    label: "Bright",
    brightness: 116,
    contrast: 104,
    saturation: 102,
    warmth: 0,
  },

  {
    key: "premium",
    label: "Premium",
    brightness: 104,
    contrast: 118,
    saturation: 108,
    warmth: 4,
  },

  {
    key: "gold",
    label: "Warm Gold",
    brightness: 106,
    contrast: 108,
    saturation: 112,
    warmth: 18,
  },

  {
    key: "diamond",
    label: "Diamond",
    brightness: 112,
    contrast: 112,
    saturation: 92,
    warmth: -8,
  },

  {
    key: "soft",
    label: "Soft",
    brightness: 108,
    contrast: 92,
    saturation: 96,
    warmth: 4,
  },
];

/* =========================================================
   HELPERS
========================================================= */

const clamp = (value, min, max) => {
  const n = Number(value);

  if (!Number.isFinite(n)) {
    return min;
  }

  return Math.max(min, Math.min(max, n));
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

const LabelItemImagesPopup = ({
  open = false,
  initialImages = [],
  onSave,
  onClose,
}) => {
  /* =======================================================
     IMAGE STATE
  ======================================================= */

  const [images, setImages] = useState([]);

  /* =======================================================
     CAMERA STATE
  ======================================================= */

  const [cameraActive, setCameraActive] = useState(false);

  const [cameraLoading, setCameraLoading] = useState(false);

  const [cameraError, setCameraError] = useState("");

  const [facingMode, setFacingMode] = useState("environment");

  /* =======================================================
     FILTER STATE
  ======================================================= */

  const [selectedFilter, setSelectedFilter] = useState("natural");

  const [brightness, setBrightness] = useState(100);

  const [contrast, setContrast] = useState(100);

  const [saturation, setSaturation] = useState(100);

  const [warmth, setWarmth] = useState(0);

  /* =======================================================
     CAPTURE PREVIEW
  ======================================================= */

  const [capturedPreview, setCapturedPreview] = useState(null);

  /* =======================================================
     REFS
  ======================================================= */

  const inputRef = useRef(null);

  /*
   * Fallback camera input.
   *
   * Kuch browsers me getUserMedia support nahi karega,
   * tab browser ka normal camera picker open hoga.
   */
  const cameraInputRef = useRef(null);

  const videoRef = useRef(null);

  const streamRef = useRef(null);

  /* =======================================================
     DERIVED
  ======================================================= */

  const canAddMore = images.length < MAX_IMAGES;

  /*
   * Browser preview ke liye CSS filter.
   *
   * Actual captured image ke andar brightness / contrast /
   * saturation canvas se permanently apply kiye jayenge.
   */
  const liveCssFilter = useMemo(() => {
    const positiveWarmth =
      warmth > 0
        ? Math.min(warmth, 30) / 100
        : 0;

    const hue =
      warmth < 0
        ? warmth / 3
        : 0;

    return `
      brightness(${brightness}%)
      contrast(${contrast}%)
      saturate(${saturation}%)
      sepia(${positiveWarmth})
      hue-rotate(${hue}deg)
    `;
  }, [
    brightness,
    contrast,
    saturation,
    warmth,
  ]);

  /* =======================================================
     STOP CAMERA
  ======================================================= */

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current
        .getTracks()
        .forEach((track) => {
          try {
            track.stop();
          } catch (error) {
            console.error(
              "Unable to stop camera track",
              error,
            );
          }
        });
    }

    streamRef.current = null;

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setCameraLoading(false);

    setCameraActive(false);
  };

  /* =======================================================
     FILTER PRESET
  ======================================================= */

  const applyPreset = (preset) => {
    setSelectedFilter(preset.key);

    setBrightness(preset.brightness);

    setContrast(preset.contrast);

    setSaturation(preset.saturation);

    setWarmth(preset.warmth);
  };

  /* =======================================================
     RESET FILTERS
  ======================================================= */

  const resetAdjustments = () => {
    applyPreset(FILTERS[0]);
  };

  /* =======================================================
     START CAMERA
  ======================================================= */

  const startCamera = async (
    nextFacingMode = facingMode,
  ) => {
    if (!canAddMore) {
      return;
    }

    setCameraError("");

    /*
     * Agar previous temporary preview hai,
     * pehle usko clean kar dete hain.
     */
    if (capturedPreview?.previewUrl) {
      URL.revokeObjectURL(
        capturedPreview.previewUrl,
      );
    }

    setCapturedPreview(null);

    /*
     * getUserMedia unavailable:
     * mobile browser ka native capture input.
     */
    if (
      !navigator.mediaDevices?.getUserMedia
    ) {
      cameraInputRef.current?.click();

      return;
    }

    /*
     * Existing stream safely stop.
     */
    if (streamRef.current) {
      streamRef.current
        .getTracks()
        .forEach((track) => {
          try {
            track.stop();
          } catch {}
        });

      streamRef.current = null;
    }

    setCameraActive(true);

    setCameraLoading(true);

    try {
      const stream =
        await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: {
              ideal: nextFacingMode,
            },

            /*
             * Browser supported ho to
             * good resolution request karenge.
             */
            width: {
              ideal: 1920,
            },

            height: {
              ideal: 1080,
            },
          },

          audio: false,
        });

      streamRef.current = stream;

      /*
       * Video element render hone ka wait.
       */
      requestAnimationFrame(() => {
        if (!videoRef.current) {
          return;
        }

        videoRef.current.srcObject =
          stream;

        videoRef.current
          .play()
          .catch(() => {});
      });

      setCameraLoading(false);
    } catch (error) {
      console.error(
        "Unable to access camera",
        error,
      );

      if (streamRef.current) {
        streamRef.current
          .getTracks()
          .forEach((track) =>
            track.stop(),
          );
      }

      streamRef.current = null;

      setCameraActive(false);

      setCameraLoading(false);

      setCameraError(
        "Camera access was not allowed. Please allow camera permission and try again.",
      );
    }
  };

  /* =======================================================
     SWITCH CAMERA
  ======================================================= */

  const switchCamera = async () => {
    const next =
      facingMode === "environment"
        ? "user"
        : "environment";

    setFacingMode(next);

    await startCamera(next);
  };

  /* =======================================================
     WARMTH OVERLAY FOR FINAL IMAGE
  ======================================================= */

  const drawWarmthOverlay = (
    ctx,
    width,
    height,
    warmthValue,
  ) => {
    if (!warmthValue) {
      return;
    }

    ctx.save();

    /*
     * Positive = warm orange tone.
     */
    if (warmthValue > 0) {
      const alpha = Math.min(
        Math.abs(warmthValue) / 180,
        0.18,
      );

      ctx.fillStyle = `rgba(255,176,92,${alpha})`;
    } else {
      /*
       * Negative = cooler blue tone.
       */
      const alpha = Math.min(
        Math.abs(warmthValue) / 180,
        0.16,
      );

      ctx.fillStyle = `rgba(90,155,255,${alpha})`;
    }

    ctx.fillRect(
      0,
      0,
      width,
      height,
    );

    ctx.restore();
  };

  /* =======================================================
     CREATE FINAL FILTERED PHOTO
  ======================================================= */

  const createFilteredBlob = (
    source,
    width,
    height,
  ) => {
    return new Promise((resolve) => {
      const canvas =
        document.createElement("canvas");

      canvas.width = width;

      canvas.height = height;

      const ctx =
        canvas.getContext("2d");

      if (!ctx) {
        resolve(null);

        return;
      }

      /*
       * Actual final file pe filter bake karenge.
       */
      ctx.filter = `
        brightness(${brightness}%)
        contrast(${contrast}%)
        saturate(${saturation}%)
      `;

      /*
       * Front camera preview mirror hota hai.
       *
       * Final image ko normal orientation me rakhenge.
       */
      ctx.drawImage(
        source,
        0,
        0,
        width,
        height,
      );

      ctx.filter = "none";

      /*
       * Warm / cool treatment.
       */
      drawWarmthOverlay(
        ctx,
        width,
        height,
        warmth,
      );

      canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        "image/jpeg",
        0.92,
      );
    });
  };

  /* =======================================================
     CAPTURE PHOTO
  ======================================================= */

  const capturePhoto = async () => {
    const video = videoRef.current;

    if (
      !video?.videoWidth ||
      !video?.videoHeight
    ) {
      setCameraError(
        "Camera is not ready yet. Please try again.",
      );

      return;
    }

    const blob =
      await createFilteredBlob(
        video,
        video.videoWidth,
        video.videoHeight,
      );

    if (!blob) {
      setCameraError(
        "Unable to capture photo. Please try again.",
      );

      return;
    }

    const previewUrl =
      URL.createObjectURL(blob);

    setCapturedPreview({
      blob,

      previewUrl,

      width: video.videoWidth,

      height: video.videoHeight,
    });
  };

  /* =======================================================
     RETAKE PHOTO
  ======================================================= */

  const retakePhoto = () => {
    if (
      capturedPreview?.previewUrl
    ) {
      URL.revokeObjectURL(
        capturedPreview.previewUrl,
      );
    }

    setCapturedPreview(null);
  };

  /* =======================================================
     USE CAPTURED PHOTO
  ======================================================= */

  const useCapturedPhoto = () => {
    if (!capturedPreview?.blob) {
      return;
    }

    const capturedAt = Date.now();

    const file = new File(
      [capturedPreview.blob],
      `jewellery-${capturedAt}.jpg`,
      {
        type: "image/jpeg",
      },
    );

    /*
     * IMPORTANT:
     *
     * Same structure rakha hai jo tumhara existing
     * LabelledStockForm expect karta hai.
     */
    const entry = {
      id: `${capturedAt}-${file.name}`,

      file,

      previewUrl:
        capturedPreview.previewUrl,

      name: file.name,

      size: file.size,

      _fromInitial: false,
    };

    setImages((previous) => [
      ...previous,
      entry,
    ].slice(0, MAX_IMAGES));

    /*
     * URL ab image entry own kar raha hai,
     * isliye yaha revoke nahi karna.
     */
    setCapturedPreview(null);

    /*
     * 5th image ke baad camera automatically close.
     */
    if (
      images.length >=
      MAX_IMAGES - 1
    ) {
      stopCamera();
    }
  };

  /* =======================================================
     INITIAL IMAGES
  ======================================================= */

  useEffect(() => {
    if (!open) {
      return;
    }

    const mapped = (
      initialImages || []
    ).map((img, index) => ({
      id:
        img.id ||
        `${Date.now()}-${index}`,

      file:
        img.file ||
        null,

      previewUrl:
        img.previewUrl ||
        (typeof img.url === "string"
          ? img.url
          : null) ||
        null,

      name:
        img.name ||
        img.originalName ||
        `Image ${index + 1}`,

      size:
        img.size ||
        0,

      /*
       * Backend / previously saved image.
       */
      _fromInitial: true,
    }));

    setImages(mapped);

    setCameraError("");

    setCameraActive(false);

    setCameraLoading(false);

    setCapturedPreview(null);

    setFacingMode("environment");

    resetAdjustments();
  }, [open]);

  /* =======================================================
     PAGE SCROLL LOCK
  ======================================================= */

  useEffect(() => {
    if (!open) {
      return;
    }

    const previous =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    return () => {
      document.body.style.overflow =
        previous;
    };
  }, [open]);

  /* =======================================================
     ESC CLOSE
  ======================================================= */

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") {
        return;
      }

      /*
       * Capture preview open hai:
       * pehle preview cancel.
       */
      if (capturedPreview) {
        retakePhoto();

        return;
      }

      /*
       * Camera open:
       * sirf camera close.
       */
      if (cameraActive) {
        stopCamera();

        return;
      }

      /*
       * Otherwise complete popup close.
       */
      handleClose();
    };

    window.addEventListener(
      "keydown",
      handleKeyDown,
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown,
      );
    };
  }, [
    open,
    cameraActive,
    capturedPreview,
  ]);

  /* =======================================================
     UNMOUNT CAMERA CLEANUP
  ======================================================= */

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current
          .getTracks()
          .forEach((track) => {
            try {
              track.stop();
            } catch {}
          });
      }

      streamRef.current = null;
    };
  }, []);

  /* =======================================================
     FILE / GALLERY SELECT
  ======================================================= */

  const handleFilesSelected = (
    event,
  ) => {
    const files = Array.from(
      event.target.files || [],
    );

    if (!files.length) {
      return;
    }

    const remainingSlots =
      MAX_IMAGES -
      images.length;

    const selectedFiles =
      files.slice(
        0,
        remainingSlots,
      );

    const now = Date.now();

    const newEntries =
      selectedFiles.map(
        (file, index) => ({
          id: `${now}-${index}-${file.name}`,

          file,

          previewUrl:
            URL.createObjectURL(
              file,
            ),

          name:
            file.name,

          size:
            file.size,

          _fromInitial: false,
        }),
      );

    setImages((previous) => [
      ...previous,
      ...newEntries,
    ]);

    /*
     * Same file dobara select kar sake.
     */
    event.target.value = "";
  };

  /* =======================================================
     REMOVE IMAGE
  ======================================================= */

  const handleRemove = (id) => {
    setImages((previous) => {
      const target =
        previous.find(
          (image) =>
            image.id === id,
        );

      /*
       * Browser-created object URL only.
       * Backend URLs revoke nahi karne.
       */
      if (
        target?.previewUrl &&
        !target?._fromInitial
      ) {
        URL.revokeObjectURL(
          target.previewUrl,
        );
      }

      return previous.filter(
        (image) =>
          image.id !== id,
      );
    });
  };

  /* =======================================================
     CLEAN SESSION URLS WHEN CANCEL
  ======================================================= */

  const cleanupSessionUrls = () => {
    images.forEach((image) => {
      if (
        image.previewUrl &&
        !image._fromInitial
      ) {
        URL.revokeObjectURL(
          image.previewUrl,
        );
      }
    });

    if (
      capturedPreview?.previewUrl
    ) {
      URL.revokeObjectURL(
        capturedPreview.previewUrl,
      );
    }
  };

  /* =======================================================
     CLOSE / CANCEL
  ======================================================= */

  const handleClose = () => {
    stopCamera();

    /*
     * Cancel kiya hai to temporary files parent me nahi jayenge,
     * therefore URLs clean kar sakte hain.
     */
    cleanupSessionUrls();

    setCapturedPreview(null);

    setImages([]);

    setCameraError("");

    onClose?.();
  };

  /* =======================================================
     SAVE
  ======================================================= */

  const handleSave = () => {
    stopCamera();

    /*
     * IMPORTANT:
     *
     * Save ke time URLs revoke nahi karenge because
     * parent component ko previewUrl chahiye ho sakta hai.
     */
    onSave?.(images);

    setCapturedPreview(null);

    setImages([]);

    onClose?.();
  };

  /* =======================================================
     NOT OPEN
  ======================================================= */

  if (!open) {
    return null;
  }

  /* =======================================================
     UI
  ======================================================= */

  return (
    <AnimatePresence>
      <motion.div
        initial={{
          opacity: 0,
        }}
        animate={{
          opacity: 1,
        }}
        exit={{
          opacity: 0,
        }}
        className="
          fixed
          inset-0
          z-[9999]

          flex
          items-end
          justify-center

          bg-slate-950/65

          backdrop-blur-sm

          sm:items-center
          sm:p-3

          md:p-4
        "
      >
        {/* =================================================
            MAIN MODAL
        ================================================= */}

        <motion.div
          initial={{
            opacity: 0,
            y: 30,
            scale: 0.98,
          }}
          animate={{
            opacity: 1,
            y: 0,
            scale: 1,
          }}
          exit={{
            opacity: 0,
            y: 20,
            scale: 0.98,
          }}
          transition={{
            duration: 0.22,
          }}
          className="
            flex
            h-[100dvh]
            w-full
            flex-col
            overflow-hidden

            bg-[#f7f8fb]

            sm:h-[94vh]
            sm:w-[96vw]
            sm:max-w-[1100px]
            sm:rounded-[26px]
            sm:shadow-2xl

            lg:h-[91vh]
            lg:max-h-[900px]
            lg:max-w-[1180px]

            xl:max-w-[1240px]
          "
        >
          {/* =================================================
              HEADER
          ================================================= */}

          <div
            className="
              flex
              shrink-0
              items-center
              justify-between
              gap-3

              border-b
              border-slate-200

              bg-white/95

              px-3
              py-3

              backdrop-blur

              sm:px-4

              lg:px-5
            "
          >
            {/* LEFT */}

            <div
              className="
                flex
                min-w-0
                items-center
                gap-2.5

                sm:gap-3
              "
            >
              <div
                className="
                  grid
                  h-10
                  w-10
                  shrink-0
                  place-items-center

                  rounded-2xl

                  bg-gradient-to-br
                  from-violet-600
                  to-indigo-600

                  text-sm
                  text-white

                  shadow-md
                  shadow-violet-200

                  sm:h-11
                  sm:w-11
                  sm:text-base
                "
              >
                <FaCamera />
              </div>

              <div className="min-w-0">
                <h2
                  className="
                    truncate

                    text-[14px]
                    font-semibold
                    text-slate-900

                    sm:text-[15px]

                    lg:text-[16px]
                  "
                >
                  Jewellery Camera
                </h2>

                <p
                  className="
                    mt-0.5

                    hidden

                    text-[10px]
                    text-slate-500

                    sm:block

                    lg:text-[11px]
                  "
                >
                  Capture professional product photos directly from stock entry
                </p>

                <p
                  className="
                    mt-0.5

                    text-[10px]
                    text-slate-500

                    sm:hidden
                  "
                >
                  Product photo studio
                </p>
              </div>
            </div>

            {/* RIGHT */}

            <div
              className="
                flex
                shrink-0
                items-center
                gap-2
              "
            >
              <div
                className="
                  rounded-full

                  bg-slate-100

                  px-2.5
                  py-1.5

                  text-[10px]
                  font-semibold
                  text-slate-600

                  sm:px-3
                  sm:text-[11px]
                "
              >
                {images.length}/{MAX_IMAGES}
              </div>

              <button
                type="button"
                onClick={handleClose}
                className="
                  grid
                  h-9
                  w-9
                  place-items-center

                  rounded-xl

                  bg-slate-100

                  text-slate-500

                  transition

                  hover:bg-rose-50
                  hover:text-rose-600

                  sm:h-10
                  sm:w-10
                "
                title="Close"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          {/* =================================================
              BODY
          ================================================= */}

          <div
            className="
              min-h-0
              flex-1
              overflow-y-auto

              p-3

              sm:p-4

              lg:p-5
            "
          >
            {/* =================================================
                NORMAL IMAGE MODE
            ================================================= */}

            {!cameraActive ? (
              <div
                className="
                  mx-auto
                  flex
                  min-h-full
                  w-full
                  max-w-[1180px]
                  flex-col
                "
              >
                {/* =============================================
                    ADD IMAGES CARD
                ============================================= */}

                <div
                  className="
                    rounded-[20px]

                    border
                    border-slate-200

                    bg-white

                    p-3

                    shadow-sm

                    sm:rounded-[24px]
                    sm:p-4

                    lg:p-5
                  "
                >
                  <div
                    className="
                      flex
                      flex-col
                      gap-1

                      sm:flex-row
                      sm:items-end
                      sm:justify-between
                      sm:gap-4
                    "
                  >
                    <div>
                      <p
                        className="
                          text-[13px]
                          font-semibold
                          text-slate-800

                          sm:text-[14px]
                        "
                      >
                        Add Product Photos
                      </p>

                      <p
                        className="
                          mt-1
                          max-w-2xl

                          text-[10px]
                          leading-5
                          text-slate-500

                          sm:text-[11px]
                        "
                      >
                        Capture jewellery directly using the camera studio or
                        choose existing photos from your device.
                      </p>
                    </div>

                    <div
                      className="
                        mt-1

                        text-[10px]
                        text-slate-400

                        sm:mt-0
                      "
                    >
                      Maximum {MAX_IMAGES} photos
                    </div>
                  </div>

                  {/* ===========================================
                      CAMERA + GALLERY
                  =========================================== */}

                  <div
                    className="
                      mt-4

                      grid
                      grid-cols-1
                      gap-3

                      sm:grid-cols-2

                      lg:gap-4
                    "
                  >
                    {/* CAMERA */}

                    <button
                      type="button"
                      disabled={!canAddMore}
                      onClick={() => {
                        setFacingMode(
                          "environment",
                        );

                        startCamera(
                          "environment",
                        );
                      }}
                      className="
                        group
                        relative
                        min-h-[150px]
                        overflow-hidden

                        rounded-[20px]

                        bg-gradient-to-br
                        from-violet-600
                        via-indigo-600
                        to-violet-700

                        p-4

                        text-left
                        text-white

                        shadow-lg
                        shadow-violet-200/70

                        transition-all
                        duration-200

                        hover:-translate-y-0.5
                        hover:shadow-xl

                        disabled:cursor-not-allowed
                        disabled:opacity-50

                        sm:min-h-[170px]
                        sm:p-5

                        lg:min-h-[185px]
                      "
                    >
                      {/* glow */}

                      <div
                        className="
                          pointer-events-none
                          absolute
                          -right-8
                          -top-10

                          h-32
                          w-32

                          rounded-full

                          bg-white/15

                          blur-3xl
                        "
                      />

                      <div
                        className="
                          relative
                          z-10

                          flex
                          h-full
                          flex-col
                          justify-between
                        "
                      >
                        <div
                          className="
                            grid
                            h-11
                            w-11
                            place-items-center

                            rounded-2xl

                            bg-white/15

                            text-lg

                            ring-1
                            ring-white/10

                            sm:h-12
                            sm:w-12
                            sm:text-xl
                          "
                        >
                          <FaCamera />
                        </div>

                        <div className="mt-5">
                          <p
                            className="
                              text-[14px]
                              font-semibold

                              sm:text-[15px]
                            "
                          >
                            Open Camera
                          </p>

                          <p
                            className="
                              mt-1

                              text-[10px]
                              leading-5
                              text-white/75

                              sm:text-[11px]
                            "
                          >
                            Capture jewellery using live filters and professional
                            camera controls.
                          </p>
                        </div>
                      </div>
                    </button>

                    {/* GALLERY */}

                    <button
                      type="button"
                      disabled={!canAddMore}
                      onClick={() =>
                        inputRef.current?.click()
                      }
                      className="
                        group
                        min-h-[150px]

                        rounded-[20px]

                        border
                        border-slate-200

                        bg-gradient-to-br
                        from-white
                        via-white
                        to-violet-50/60

                        p-4

                        text-left

                        shadow-sm

                        transition-all
                        duration-200

                        hover:-translate-y-0.5
                        hover:border-violet-200
                        hover:shadow-md

                        disabled:cursor-not-allowed
                        disabled:opacity-50

                        sm:min-h-[170px]
                        sm:p-5

                        lg:min-h-[185px]
                      "
                    >
                      <div
                        className="
                          flex
                          h-full
                          flex-col
                          justify-between
                        "
                      >
                        <div
                          className="
                            grid
                            h-11
                            w-11
                            place-items-center

                            rounded-2xl

                            bg-violet-50

                            text-lg
                            text-violet-600

                            ring-1
                            ring-violet-100

                            transition

                            group-hover:bg-violet-100

                            sm:h-12
                            sm:w-12
                            sm:text-xl
                          "
                        >
                          <FaImages />
                        </div>

                        <div className="mt-5">
                          <p
                            className="
                              text-[14px]
                              font-semibold
                              text-slate-800

                              sm:text-[15px]
                            "
                          >
                            Choose Gallery
                          </p>

                          <p
                            className="
                              mt-1

                              text-[10px]
                              leading-5
                              text-slate-500

                              sm:text-[11px]
                            "
                          >
                            Select existing jewellery images already available on
                            this device.
                          </p>
                        </div>
                      </div>
                    </button>
                  </div>

                  {/* FILE INPUT */}

                  <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={
                      handleFilesSelected
                    }
                    disabled={!canAddMore}
                    className="hidden"
                  />

                  {/* CAMERA FALLBACK INPUT */}

                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={
                      handleFilesSelected
                    }
                    disabled={!canAddMore}
                    className="hidden"
                  />

                  {/* CAMERA ERROR */}

                  {cameraError && (
                    <div
                      className="
                        mt-4

                        rounded-2xl

                        bg-rose-50

                        px-3
                        py-2.5

                        text-[10px]
                        leading-5
                        text-rose-700

                        ring-1
                        ring-rose-100

                        sm:text-[11px]
                      "
                    >
                      {cameraError}
                    </div>
                  )}
                </div>

                {/* =============================================
                    PRODUCT PHOTO LIST
                ============================================= */}

                <div
                  className="
                    mt-3

                    rounded-[20px]

                    border
                    border-slate-200

                    bg-white

                    p-3

                    shadow-sm

                    sm:mt-4
                    sm:rounded-[24px]
                    sm:p-4

                    lg:p-5
                  "
                >
                  {/* TITLE */}

                  <div
                    className="
                      flex
                      items-center
                      justify-between
                      gap-3
                    "
                  >
                    <div>
                      <p
                        className="
                          text-[13px]
                          font-semibold
                          text-slate-800

                          sm:text-[14px]
                        "
                      >
                        Product Photos
                      </p>

                      <p
                        className="
                          mt-0.5

                          text-[10px]
                          text-slate-500
                        "
                      >
                        Photos attached to this stock item
                      </p>
                    </div>

                    {images.length >=
                      MAX_IMAGES && (
                      <span
                        className="
                          rounded-full

                          bg-rose-50

                          px-2.5
                          py-1

                          text-[9px]
                          font-semibold
                          text-rose-600

                          ring-1
                          ring-rose-100

                          sm:text-[10px]
                        "
                      >
                        Limit reached
                      </span>
                    )}
                  </div>

                  {/* IMAGES */}

                  {images.length ? (
                    <div
                      className="
                        mt-3

                        grid
                        grid-cols-2
                        gap-2.5

                        sm:grid-cols-3
                        sm:gap-3

                        md:grid-cols-4

                        xl:grid-cols-5
                      "
                    >
                      {images.map(
                        (image, index) => (
                          <div
                            key={image.id}
                            className="
                              group
                              relative
                              overflow-hidden

                              rounded-2xl

                              border
                              border-slate-200

                              bg-slate-50

                              shadow-sm
                            "
                          >
                            {/* IMAGE NUMBER */}

                            <div
                              className="
                                absolute
                                left-2
                                top-2
                                z-10

                                grid
                                h-6
                                min-w-6
                                place-items-center

                                rounded-full

                                bg-black/55

                                px-1.5

                                text-[9px]
                                font-semibold
                                text-white

                                backdrop-blur
                              "
                            >
                              {index + 1}
                            </div>

                            {/* PREVIEW */}

                            {image.previewUrl ? (
                              <img
                                src={
                                  image.previewUrl
                                }
                                alt={
                                  image.name
                                }
                                className="
                                  aspect-square
                                  w-full

                                  bg-slate-100

                                  object-cover
                                "
                              />
                            ) : (
                              <div
                                className="
                                  grid
                                  aspect-square
                                  w-full
                                  place-items-center

                                  text-[10px]
                                  text-slate-400
                                "
                              >
                                No preview
                              </div>
                            )}

                            {/* REMOVE */}

                            <button
                              type="button"
                              onClick={() =>
                                handleRemove(
                                  image.id,
                                )
                              }
                              className="
                                absolute
                                right-2
                                top-2
                                z-10

                                grid
                                h-8
                                w-8
                                place-items-center

                                rounded-full

                                bg-black/60

                                text-white

                                backdrop-blur

                                transition

                                hover:bg-rose-600
                              "
                              title="Remove image"
                            >
                              <FaTrash
                                size={11}
                              />
                            </button>

                            {/* META */}

                            <div
                              className="
                                border-t
                                border-slate-100

                                bg-white

                                px-2
                                py-2
                              "
                            >
                              <p
                                className="
                                  truncate

                                  text-[10px]
                                  font-medium
                                  text-slate-700
                                "
                              >
                                {image.name}
                              </p>

                              {!!image.size && (
                                <p
                                  className="
                                    mt-0.5

                                    text-[9px]
                                    text-slate-400
                                  "
                                >
                                  {(
                                    image.size /
                                    1024
                                  ).toFixed(
                                    0,
                                  )}{" "}
                                  KB
                                </p>
                              )}
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  ) : (
                    /* EMPTY */

                    <div
                      className="
                        mt-3

                        rounded-2xl

                        border
                        border-dashed
                        border-slate-300

                        bg-slate-50/60

                        py-8

                        text-center

                        sm:py-10
                      "
                    >
                      <FaImages
                        className="
                          mx-auto

                          text-2xl
                          text-slate-300
                        "
                      />

                      <p
                        className="
                          mt-2

                          text-[11px]
                          font-medium
                          text-slate-500

                          sm:text-[12px]
                        "
                      >
                        No product photos yet
                      </p>

                      <p
                        className="
                          mt-1

                          text-[9px]
                          text-slate-400

                          sm:text-[10px]
                        "
                      >
                        Open camera or choose photos from gallery
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* =================================================
                  CAMERA STUDIO
              ================================================= */

              <div
                className="
                  mx-auto

                  grid
                  min-h-full
                  w-full
                  max-w-[1180px]
                  grid-cols-1
                  gap-3

                  lg:grid-cols-[minmax(0,1fr)_280px]

                  xl:grid-cols-[minmax(0,1fr)_320px]
                "
              >
                {/* =============================================
                    CAMERA
                ============================================= */}

                <div
                  className="
                    flex
                    min-h-[62vh]
                    flex-col
                    overflow-hidden

                    rounded-[20px]

                    bg-black

                    shadow-xl

                    sm:min-h-[620px]
                    sm:rounded-[24px]

                    lg:min-h-[680px]
                  "
                >
                  {/* CAMERA HEADER */}

                  <div
                    className="
                      flex
                      shrink-0
                      items-center
                      justify-between
                      gap-3

                      bg-slate-950

                      px-3
                      py-2.5

                      text-white

                      sm:px-4
                      sm:py-3
                    "
                  >
                    <div className="min-w-0">
                      <p
                        className="
                          text-[11px]
                          font-semibold

                          sm:text-[12px]
                        "
                      >
                        Live Camera
                      </p>

                      <p
                        className="
                          mt-0.5
                          truncate

                          text-[9px]
                          text-white/50

                          sm:text-[10px]
                        "
                      >
                        Keep jewellery centered inside the frame
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={
                        switchCamera
                      }
                      disabled={
                        cameraLoading ||
                        !!capturedPreview
                      }
                      className="
                        inline-flex
                        min-h-[38px]
                        shrink-0
                        items-center
                        gap-2

                        rounded-xl

                        bg-white/10

                        px-3

                        text-[10px]
                        font-semibold

                        ring-1
                        ring-white/10

                        transition

                        hover:bg-white/15

                        disabled:opacity-40
                      "
                    >
                      <FaSyncAlt />

                      <span
                        className="
                          hidden
                          sm:inline
                        "
                      >
                        Switch Camera
                      </span>

                      <span
                        className="
                          sm:hidden
                        "
                      >
                        Switch
                      </span>
                    </button>
                  </div>

                  {/* ===========================================
                      LIVE VIEW
                  =========================================== */}

                  <div
                    className="
                      relative
                      min-h-0
                      flex-1
                      overflow-hidden

                      bg-black
                    "
                  >
                    {/* LOADING */}

                    {cameraLoading && (
                      <div
                        className="
                          absolute
                          inset-0
                          z-30

                          grid
                          place-items-center

                          bg-black

                          text-white
                        "
                      >
                        <div className="text-center">
                          <div
                            className="
                              mx-auto

                              h-8
                              w-8

                              animate-spin

                              rounded-full

                              border-2
                              border-white
                              border-t-transparent
                            "
                          />

                          <p
                            className="
                              mt-3
                              text-[11px]
                            "
                          >
                            Starting camera...
                          </p>
                        </div>
                      </div>
                    )}

                    {/* LIVE VIDEO */}

                    {!capturedPreview ? (
                      <video
                        ref={videoRef}
                        autoPlay
                        muted
                        playsInline
                        className="
                          absolute
                          inset-0

                          h-full
                          w-full

                          object-cover

                          transition
                        "
                        style={{
                          filter:
                            liveCssFilter,

                          transform:
                            facingMode ===
                            "user"
                              ? "scaleX(-1)"
                              : "none",
                        }}
                      />
                    ) : (
                      /* CAPTURED PREVIEW */

                      <img
                        src={
                          capturedPreview.previewUrl
                        }
                        alt="Captured jewellery"
                        className="
                          absolute
                          inset-0

                          h-full
                          w-full

                          object-cover
                        "
                      />
                    )}

                    {/* =========================================
                        JEWELLERY GUIDE
                    ========================================= */}

                    <div
                      className="
                        pointer-events-none
                        absolute
                        inset-0
                        z-10

                        flex
                        items-center
                        justify-center

                        p-5

                        sm:p-8

                        md:p-10

                        lg:p-12
                      "
                    >
                      <div
                        className="
                          relative

                          aspect-[4/3]
                          w-full
                          max-w-[620px]

                          rounded-[26px]

                          border
                          border-white/35

                          shadow-[0_0_0_999px_rgba(0,0,0,0.14)]

                          sm:rounded-[32px]
                        "
                      >
                        {/* TOP LEFT */}

                        <span
                          className="
                            absolute
                            left-0
                            top-0

                            h-9
                            w-9

                            rounded-tl-[24px]

                            border-l-[3px]
                            border-t-[3px]
                            border-white

                            sm:h-11
                            sm:w-11
                            sm:rounded-tl-[28px]
                            sm:border-l-4
                            sm:border-t-4
                          "
                        />

                        {/* TOP RIGHT */}

                        <span
                          className="
                            absolute
                            right-0
                            top-0

                            h-9
                            w-9

                            rounded-tr-[24px]

                            border-r-[3px]
                            border-t-[3px]
                            border-white

                            sm:h-11
                            sm:w-11
                            sm:rounded-tr-[28px]
                            sm:border-r-4
                            sm:border-t-4
                          "
                        />

                        {/* BOTTOM LEFT */}

                        <span
                          className="
                            absolute
                            bottom-0
                            left-0

                            h-9
                            w-9

                            rounded-bl-[24px]

                            border-b-[3px]
                            border-l-[3px]
                            border-white

                            sm:h-11
                            sm:w-11
                            sm:rounded-bl-[28px]
                            sm:border-b-4
                            sm:border-l-4
                          "
                        />

                        {/* BOTTOM RIGHT */}

                        <span
                          className="
                            absolute
                            bottom-0
                            right-0

                            h-9
                            w-9

                            rounded-br-[24px]

                            border-b-[3px]
                            border-r-[3px]
                            border-white

                            sm:h-11
                            sm:w-11
                            sm:rounded-br-[28px]
                            sm:border-b-4
                            sm:border-r-4
                          "
                        />

                        {/* GRID HORIZONTAL */}

                        <div
                          className="
                            absolute
                            inset-x-0
                            top-1/2

                            border-t
                            border-white/15
                          "
                        />

                        {/* GRID VERTICAL */}

                        <div
                          className="
                            absolute
                            inset-y-0
                            left-1/2

                            border-l
                            border-white/15
                          "
                        />

                        {/* GUIDE LABEL */}

                        {!capturedPreview && (
                          <div
                            className="
                              absolute
                              bottom-3
                              left-1/2

                              -translate-x-1/2

                              whitespace-nowrap

                              rounded-full

                              bg-black/35

                              px-3
                              py-1

                              text-[8px]
                              font-medium
                              text-white

                              backdrop-blur

                              sm:text-[9px]
                            "
                          >
                            Position jewellery inside frame
                          </div>
                        )}

                        {/* CAPTURED LABEL */}

                        {capturedPreview && (
                          <div
                            className="
                              absolute
                              left-1/2
                              top-3

                              -translate-x-1/2

                              rounded-full

                              bg-emerald-500/90

                              px-3
                              py-1

                              text-[9px]
                              font-semibold
                              text-white

                              shadow-lg
                            "
                          >
                            Photo Preview
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ===========================================
                      CAMERA BOTTOM
                  =========================================== */}

                  <div
                    className="
                      shrink-0

                      bg-slate-950

                      px-3
                      py-3

                      sm:px-4
                    "
                  >
                    {!capturedPreview ? (
                      <div
                        className="
                          grid
                          grid-cols-[1fr_auto_1fr]
                          items-center
                          gap-3
                        "
                      >
                        {/* CLOSE */}

                        <div>
                          <button
                            type="button"
                            onClick={
                              stopCamera
                            }
                            className="
                              inline-flex
                              min-h-[44px]
                              items-center
                              justify-center
                              gap-2

                              rounded-xl

                              bg-white/10

                              px-3

                              text-[10px]
                              font-semibold
                              text-white

                              ring-1
                              ring-white/10

                              transition

                              hover:bg-white/15

                              sm:px-4
                              sm:text-[11px]
                            "
                          >
                            <FaTimes />

                            Close
                          </button>
                        </div>

                        {/* SHUTTER */}

                        <button
                          type="button"
                          onClick={
                            capturePhoto
                          }
                          disabled={
                            cameraLoading
                          }
                          className="
                            relative

                            grid
                            h-[66px]
                            w-[66px]
                            shrink-0
                            place-items-center

                            rounded-full

                            bg-white

                            shadow-[0_0_0_6px_rgba(255,255,255,0.17)]

                            transition

                            active:scale-95

                            disabled:opacity-50

                            sm:h-[72px]
                            sm:w-[72px]
                          "
                          aria-label="Capture photo"
                        >
                          <span
                            className="
                              h-[50px]
                              w-[50px]

                              rounded-full

                              border-[3px]
                              border-violet-600

                              sm:h-[56px]
                              sm:w-[56px]
                            "
                          />
                        </button>

                        {/* COUNT */}

                        <div
                          className="
                            justify-self-end
                            text-right
                          "
                        >
                          <p
                            className="
                              text-[9px]
                              text-white/50

                              sm:text-[10px]
                            "
                          >
                            Photos
                          </p>

                          <p
                            className="
                              text-[14px]
                              font-semibold
                              text-white
                            "
                          >
                            {images.length}/
                            {MAX_IMAGES}
                          </p>
                        </div>
                      </div>
                    ) : (
                      /* =======================================
                          AFTER CAPTURE
                      ======================================= */

                      <div
                        className="
                          grid
                          grid-cols-2
                          gap-2

                          sm:gap-3
                        "
                      >
                        {/* RETAKE */}

                        <button
                          type="button"
                          onClick={
                            retakePhoto
                          }
                          className="
                            inline-flex
                            min-h-[46px]
                            items-center
                            justify-center
                            gap-2

                            rounded-xl

                            bg-white/10

                            px-3

                            text-[11px]
                            font-semibold
                            text-white

                            ring-1
                            ring-white/10

                            transition

                            hover:bg-white/15
                          "
                        >
                          <FaRedo />

                          Retake
                        </button>

                        {/* USE */}

                        <button
                          type="button"
                          onClick={
                            useCapturedPhoto
                          }
                          className="
                            inline-flex
                            min-h-[46px]
                            items-center
                            justify-center
                            gap-2

                            rounded-xl

                            bg-emerald-500

                            px-3

                            text-[11px]
                            font-semibold
                            text-white

                            shadow-lg
                            shadow-emerald-900/20

                            transition

                            hover:bg-emerald-600
                          "
                        >
                          <FaCheck />

                          Use Photo
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* =============================================
                    FILTER PANEL
                ============================================= */}

                <div
                  className="
                    h-fit

                    rounded-[20px]

                    border
                    border-slate-200

                    bg-white

                    p-3

                    shadow-sm

                    sm:rounded-[24px]
                    sm:p-4

                    lg:sticky
                    lg:top-0
                  "
                >
                  {/* HEADER */}

                  <div
                    className="
                      flex
                      items-center
                      justify-between
                      gap-2
                    "
                  >
                    <div>
                      <p
                        className="
                          text-[12px]
                          font-semibold
                          text-slate-800

                          sm:text-[13px]
                        "
                      >
                        Jewellery Filters
                      </p>

                      <p
                        className="
                          mt-0.5

                          text-[9px]
                          text-slate-500

                          sm:text-[10px]
                        "
                      >
                        Adjust before taking photo
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={
                        resetAdjustments
                      }
                      disabled={
                        !!capturedPreview
                      }
                      className="
                        inline-flex
                        items-center
                        gap-1.5

                        rounded-lg

                        bg-slate-100

                        px-2.5
                        py-1.5

                        text-[9px]
                        font-semibold
                        text-slate-600

                        transition

                        hover:bg-slate-200

                        disabled:opacity-40
                      "
                    >
                      <FaUndoAlt />

                      Reset
                    </button>
                  </div>

                  {/* ===========================================
                      FILTER PRESETS
                  =========================================== */}

                  <div
                    className="
                      mt-3

                      flex
                      gap-2
                      overflow-x-auto

                      pb-1

                      [-ms-overflow-style:none]
                      [scrollbar-width:none]
                      [&::-webkit-scrollbar]:hidden

                      lg:grid
                      lg:grid-cols-2
                      lg:overflow-visible
                      lg:pb-0
                    "
                  >
                    {FILTERS.map(
                      (filter) => {
                        const active =
                          selectedFilter ===
                          filter.key;

                        return (
                          <button
                            type="button"
                            key={
                              filter.key
                            }
                            disabled={
                              !!capturedPreview
                            }
                            onClick={() =>
                              applyPreset(
                                filter,
                              )
                            }
                            className={[
                              `
                                min-w-[95px]
                                shrink-0

                                rounded-xl

                                px-3
                                py-2.5

                                text-left

                                transition

                                disabled:opacity-50

                                lg:min-w-0
                              `,

                              active
                                ? `
                                    bg-violet-600

                                    text-white

                                    shadow-md
                                    shadow-violet-200
                                  `
                                : `
                                    bg-slate-50

                                    text-slate-600

                                    ring-1
                                    ring-slate-200

                                    hover:bg-violet-50
                                    hover:text-violet-700
                                  `,
                            ].join(
                              " ",
                            )}
                          >
                            <p
                              className="
                                text-[10px]
                                font-semibold
                              "
                            >
                              {
                                filter.label
                              }
                            </p>
                          </button>
                        );
                      },
                    )}
                  </div>

                  {/* ===========================================
                      ADJUSTMENTS
                  =========================================== */}

                  <div
                    className="
                      mt-4

                      grid
                      grid-cols-1
                      gap-4

                      sm:grid-cols-2

                      lg:grid-cols-1
                    "
                  >
                    <ControlSlider
                      label="Brightness"
                      value={brightness}
                      min={70}
                      max={140}
                      display={`${brightness}%`}
                      disabled={
                        !!capturedPreview
                      }
                      onChange={(
                        value,
                      ) => {
                        setBrightness(
                          value,
                        );

                        setSelectedFilter(
                          "custom",
                        );
                      }}
                    />

                    <ControlSlider
                      label="Contrast"
                      value={contrast}
                      min={70}
                      max={140}
                      display={`${contrast}%`}
                      disabled={
                        !!capturedPreview
                      }
                      onChange={(
                        value,
                      ) => {
                        setContrast(
                          value,
                        );

                        setSelectedFilter(
                          "custom",
                        );
                      }}
                    />

                    <ControlSlider
                      label="Saturation"
                      value={saturation}
                      min={60}
                      max={150}
                      display={`${saturation}%`}
                      disabled={
                        !!capturedPreview
                      }
                      onChange={(
                        value,
                      ) => {
                        setSaturation(
                          value,
                        );

                        setSelectedFilter(
                          "custom",
                        );
                      }}
                    />

                    <ControlSlider
                      label="Warmth"
                      value={warmth}
                      min={-25}
                      max={30}
                      display={
                        warmth > 0
                          ? `+${warmth}`
                          : warmth
                      }
                      disabled={
                        !!capturedPreview
                      }
                      onChange={(
                        value,
                      ) => {
                        setWarmth(
                          value,
                        );

                        setSelectedFilter(
                          "custom",
                        );
                      }}
                    />
                  </div>

                  {/* ===========================================
                      ACTIVE FILTER
                  =========================================== */}

                  <div
                    className="
                      mt-4

                      flex
                      items-center
                      justify-between
                      gap-2

                      rounded-xl

                      bg-violet-50

                      px-3
                      py-2

                      ring-1
                      ring-violet-100
                    "
                  >
                    <span
                      className="
                        text-[9px]
                        text-violet-500
                      "
                    >
                      Current look
                    </span>

                    <span
                      className="
                        text-[10px]
                        font-semibold
                        text-violet-700
                      "
                    >
                      {selectedFilter ===
                      "custom"
                        ? "Custom"
                        : FILTERS.find(
                              (filter) =>
                                filter.key ===
                                selectedFilter,
                            )
                            ?.label ||
                          "Natural"}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* =================================================
              FOOTER
          ================================================= */}

          {!cameraActive && (
            <div
              className="
                shrink-0

                border-t
                border-slate-200

                bg-white

                px-3
                py-3

                sm:px-4

                lg:px-5
              "
            >
              <div
                className="
                  mx-auto
                  flex
                  max-w-[1180px]
                  items-center
                  justify-between
                  gap-3
                "
              >
                <p
                  className="
                    hidden

                    text-[10px]
                    text-slate-500

                    md:block
                  "
                >
                  {images.length
                    ? `${images.length} photo${images.length > 1 ? "s" : ""} ready to attach`
                    : "Add at least one product photo to save"}
                </p>

                <div
                  className="
                    grid
                    w-full
                    grid-cols-2
                    gap-2

                    md:ml-auto
                    md:flex
                    md:w-auto
                  "
                >
                  {/* CANCEL */}

                  <button
                    type="button"
                    onClick={
                      handleClose
                    }
                    className="
                      min-h-[44px]

                      rounded-xl

                      border
                      border-slate-200

                      bg-white

                      px-4

                      text-[11px]
                      font-semibold
                      text-slate-600

                      transition

                      hover:bg-slate-50
                      hover:text-slate-900

                      md:min-w-[100px]
                    "
                  >
                    Cancel
                  </button>

                  {/* SAVE */}

                  <button
                    type="button"
                    onClick={
                      handleSave
                    }
                    disabled={
                      images.length === 0
                    }
                    className="
                      min-h-[44px]

                      rounded-xl

                      bg-violet-600

                      px-5

                      text-[11px]
                      font-semibold
                      text-white

                      shadow-md
                      shadow-violet-200

                      transition

                      hover:bg-violet-700
                      hover:shadow-lg

                      disabled:cursor-not-allowed
                      disabled:opacity-40

                      md:min-w-[150px]
                    "
                  >
                    Save Photos ({images.length})
                  </button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/* =========================================================
   SLIDER COMPONENT
========================================================= */

const ControlSlider = ({
  label,
  value,
  min,
  max,
  display,
  disabled = false,
  onChange,
}) => {
  return (
    <div
      className={
        disabled
          ? "opacity-50"
          : ""
      }
    >
      <div
        className="
          mb-1.5

          flex
          items-center
          justify-between
          gap-2
        "
      >
        <label
          className="
            text-[10px]
            font-medium
            text-slate-600
          "
        >
          {label}
        </label>

        <span
          className="
            min-w-[38px]

            rounded-full

            bg-slate-100

            px-2
            py-0.5

            text-center

            text-[9px]
            font-semibold
            text-slate-500
          "
        >
          {display}
        </span>
      </div>

      <input
        type="range"
        min={min}
        max={max}
        value={value}
        disabled={disabled}
        onChange={(event) => {
          onChange(
            clamp(
              event.target.value,
              min,
              max,
            ),
          );
        }}
        className="
          h-1.5
          w-full

          cursor-pointer

          accent-violet-600

          disabled:cursor-not-allowed
        "
      />
    </div>
  );
};

export default LabelItemImagesPopup;